import React, { useState, useEffect } from 'react';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock, AlertCircle, ShieldCheck, BarChart3 } from 'lucide-react';
import { useStore } from '../hooks/useStore';
import { can, firstAllowedRoute, ROUTE_PERMISSIONS } from '../utils/permissions';
import { toast } from 'sonner';

/**
 * Pagina di Login — rifacimento grafico "dark sidebar".
 *
 * Layout split full-screen: pannello brand scuro a sinistra (desktop),
 * card di accesso chiara a destra. La logica (submit, store, redirect,
 * toggle password, gestione errori) è invariata rispetto alla versione
 * precedente: cambia SOLO la presentazione.
 */

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Redirect back to the page the user was trying to access, or /dashboard
  const from = (location.state as any)?.from?.pathname || '/dashboard';

  const { login, isAuthenticated, isLoading, error, clearError, user } = useStore();

  // RBAC: "/dashboard" come default va bene solo per chi può vederla. Se il
  // "from" (o il default) non è tra le pagine permesse, atterra sulla prima
  // consentita — es. il collaboratore "solo traduzioni" entra su /languages.
  const target = (() => {
    const routeKey = Object.keys(ROUTE_PERMISSIONS).find(
      (r) => from === r || from.startsWith(r + '/')
    );
    if (routeKey && can(user, ...ROUTE_PERMISSIONS[routeKey])) return from;
    // Zero route permesse → /dashboard, dove RequirePerm mostra la
    // schermata "nessuna sezione disponibile".
    return firstAllowedRoute(user) ?? '/dashboard';
  })();

  // Clear error when component mounts or when inputs change
  useEffect(() => {
    clearError();
  }, [email, password, clearError]);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate(target, { replace: true });
    }
  }, [isAuthenticated, navigate, target]);

  if (isAuthenticated) {
    return <Navigate to={target} replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isLoading) return; // Prevent multiple submissions

    if (!email.trim() || !password.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    setIsSubmitting(true);
    try {
      await login(email.trim(), password);
      toast.success('Login successful!');
    } catch (error: any) {
      toast.error(error.message || 'Login failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const busy = isLoading || isSubmitting;

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#f4f5f7] text-slate-800">
      {/* ── Pannello brand (solo desktop) ─────────────────────────────── */}
      <div className="relative hidden lg:flex flex-col justify-between overflow-hidden bg-[#0f1426] text-white p-12">
        {/* Glow indaco soffuso */}
        <div className="pointer-events-none absolute -top-24 -left-24 h-96 w-96 rounded-full bg-indigo-500/20 blur-3xl" />
        <div className="pointer-events-none absolute bottom-0 right-0 h-80 w-80 rounded-full bg-violet-500/10 blur-3xl" />

        {/* Brand */}
        <div className="relative flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-500 grid place-items-center text-white font-extrabold shadow-lg shadow-indigo-500/30">
            L
          </div>
          <div className="leading-tight">
            <p className="font-bold text-white text-lg">Locally</p>
            <p className="text-[11px] text-slate-400 -mt-0.5">Admin</p>
          </div>
        </div>

        {/* Claim + feature bullets */}
        <div className="relative max-w-sm">
          <h1 className="text-3xl font-extrabold leading-tight tracking-tight">
            Dashboard di amministrazione
          </h1>
          <p className="mt-3 text-sm text-slate-400">
            Gestisci utenti, abbonamenti e configurazioni della piattaforma da un'unica console.
          </p>

          <ul className="mt-8 space-y-4">
            <li className="flex items-start gap-3">
              <span className="mt-0.5 h-8 w-8 shrink-0 grid place-items-center rounded-lg bg-white/5 border border-white/10 text-indigo-300">
                <BarChart3 className="h-4 w-4" />
              </span>
              <div>
                <p className="text-sm font-semibold text-white">Panoramica in tempo reale</p>
                <p className="text-xs text-slate-400">Metriche su utenti, ruoli e traduzioni.</p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="mt-0.5 h-8 w-8 shrink-0 grid place-items-center rounded-lg bg-white/5 border border-white/10 text-indigo-300">
                <ShieldCheck className="h-4 w-4" />
              </span>
              <div>
                <p className="text-sm font-semibold text-white">Accesso riservato</p>
                <p className="text-xs text-slate-400">Area protetta agli amministratori.</p>
              </div>
            </li>
          </ul>
        </div>

        {/* Footer */}
        <p className="relative text-xs text-slate-500">Locally Admin Dashboard v1.0</p>
      </div>

      {/* ── Pannello accesso ──────────────────────────────────────────── */}
      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md">
          {/* Brand compatto (solo mobile) */}
          <div className="lg:hidden flex items-center justify-center gap-3 mb-8">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-500 grid place-items-center text-white font-extrabold shadow-lg shadow-indigo-500/30">
              L
            </div>
            <p className="font-bold text-slate-900 text-lg">Locally</p>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
            <h2 className="text-2xl font-extrabold text-slate-900">Accedi</h2>
            <p className="mt-1 text-sm text-slate-500">
              Inserisci le tue credenziali per accedere alla dashboard.
            </p>

            <form onSubmit={handleSubmit} className="mt-6 space-y-5">
              {/* Errore */}
              {error && (
                <div className="bg-rose-50 border border-rose-200 rounded-lg p-4 flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-rose-500 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-rose-700">{error}</div>
                </div>
              )}

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1.5">
                  Email
                </label>
                <div className="relative">
                  <span className="pointer-events-none absolute inset-y-0 left-0 pl-3 flex items-center text-slate-400">
                    <Mail className="h-5 w-5" />
                  </span>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={busy}
                    className="w-full h-11 pl-10 pr-3 rounded-lg border border-slate-300 bg-white text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/40 focus:border-indigo-500 disabled:bg-slate-50 disabled:text-slate-500"
                    placeholder="admin@locally.it"
                    autoComplete="email"
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-1.5">
                  Password
                </label>
                <div className="relative">
                  <span className="pointer-events-none absolute inset-y-0 left-0 pl-3 flex items-center text-slate-400">
                    <Lock className="h-5 w-5" />
                  </span>
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={busy}
                    className="w-full h-11 pl-10 pr-10 rounded-lg border border-slate-300 bg-white text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/40 focus:border-indigo-500 disabled:bg-slate-50 disabled:text-slate-500"
                    placeholder="Inserisci la password"
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={togglePasswordVisibility}
                    disabled={busy}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600 disabled:hover:text-slate-400"
                    aria-label={showPassword ? 'Nascondi password' : 'Mostra password'}
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={busy || !email.trim() || !password.trim()}
                className="w-full h-11 flex justify-center items-center gap-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-semibold focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-slate-300 disabled:text-slate-500 disabled:cursor-not-allowed transition-colors"
              >
                {busy ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    Accesso in corso…
                  </>
                ) : (
                  'Accedi'
                )}
              </button>
            </form>

            {/* Footer card */}
            <p className="mt-6 text-center text-xs text-slate-400">
              Accesso riservato agli amministratori. Contatta il responsabile di sistema in caso di problemi.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
